import React, { useState, useEffect, useCallback } from "react";
import {
  BreadcrumbGroup,
  SpaceBetween,
  Button,
  ContentLayout,
  Box,
  Container,
  Header,
  Table,
  TextFilter,
  Pagination,
  Badge,
  StatusIndicator,
  Link,
  DateRangePicker,
  Select,
  Icon,
  ButtonDropdown,
  ColumnLayout,
  Multiselect,
  Alert,
} from "@amzn/awsui-components-console";
import { useParams, useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { getUserAtom } from "../../contexts/userContext";
import { makeApiCall } from "../../utils/api";
import { API_PATHS } from "../../config/api-endpoints";
import { getAndesStatus } from "../../config/andesStatus";
import { useProfile } from "../../hooks/useProfiles";
import NProgress from "nprogress";
import { JobRunStatus, isFailureStatus, RunType } from "../../types/jobRunStatus";
import "./JobHistory.scss";

interface JobRun {
  jobId: string;
  runId: string;
  runType: RunType;
  status: JobRunStatus;
  ldap?: string;
  startedAt: string;
  completedAt?: string;
  triggerSource?: string;
  scheduleId?: string;
  scheduleName?: string;
  codeGenerationOutput?: {
    sqlPath?: string;
    outputLocation?: string;
  };
  errorMessage?: string;
  createdBy: string;
  jobName?: string;
  description?: string;
  profileId?: string;
  jobType?: string;
  requestDetails?: Record<string, any>;
  responseDetails?: Record<string, any>;
  persistenceStatus?: "PERSISTED" | "SKIPPED" | "FAILED";
  persistenceError?: string;
  partitionValue?: string;
  andesRegistrationStatus?:
    | "CREATED"
    | "UNRELEASED"
    | "ACTIVE"
    | "DEPRECATED"
    | "ARCHIVED"
    | "DELETED"
    | "REGISTERED"
    | "FAILED";
  andesRegistrationError?: string;
  datasetUrl?: string;
}

const JobHistory: React.FC = () => {
  const { id: profileId, jobId } = useParams();
  const navigate = useNavigate();
  const [authenticatedUser] = useAtom(getUserAtom);

  // Fetch profile data from API
  const {
    profile: profileData,
    loading: profileLoading,
    error: profileError,
  } = useProfile(profileId || null);

  // State management
  const [jobRuns, setJobRuns] = useState<JobRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [dateRange, setDateRange] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [triggerFilter, setTriggerFilter] = useState<string[]>([]);
  const [sortingColumn, setSortingColumn] = useState<any>({
    sortingField: "startedAt",
    sortingDescending: true,
  });
  const [publishingRunId, setPublishingRunId] = useState<string | null>(null);

  // Shared publish/retry flow — only the error message differs.
  const startPublishFlow = async (runId: string, jobId: string, errorMessage: string) => {
    try {
      setPublishingRunId(runId);
      NProgress.start();
      await makeApiCall(API_PATHS.JOBS.RUN, "POST", {
        jobId,
        runId,
        runType: "PUBLISH",
      });
      await fetchJobRuns(false, true);
    } catch (err: any) {
      setError(err.message || errorMessage);
    } finally {
      setPublishingRunId(null);
      NProgress.done();
    }
  };

  // Fetch job runs from API
  const fetchJobRuns = useCallback(
    async (loadMore = false, silent = false) => {
      if (!jobId || !authenticatedUser) return;

      if (!silent) {
        setLoading(true);
        NProgress.start();
        setError(null);
      }

      try {
        const response = await makeApiCall(API_PATHS.JOBS.LIST_RUNS, "POST", {
          jobId: jobId,
          limit: 50,
          ...(loadMore && nextToken ? { nextToken } : {}),
        });

        if (response.jobRuns) {
          if (loadMore) {
            setJobRuns((prev) => [...prev, ...response.jobRuns]);
          } else {
            setJobRuns(response.jobRuns);
          }

          setNextToken(response.nextToken || null);
          setHasMore(!!response.nextToken);
        }
      } catch (err: any) {
        console.error("Failed to fetch job runs:", err);
        if (!silent) {
          setError(err.message || "Failed to load job runs");
        }
      } finally {
        if (!silent) {
          setLoading(false);
          NProgress.done();
        }
      }
    },
    [jobId, authenticatedUser, nextToken],
  );

  // Initial load
  useEffect(() => {
    fetchJobRuns();
  }, [jobId, authenticatedUser?.username, fetchJobRuns]);

  // Refresh job runs
  const handleRefresh = () => {
    fetchJobRuns();
  };

  // Load more job runs
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchJobRuns(true);
    }
  };

  // Filter job runs
  const filteredRuns = jobRuns.filter((run) => {
    // Text filter
    const matchesText =
      !filteringText ||
      run.runId.toLowerCase().includes(filteringText.toLowerCase()) ||
      run.createdBy.toLowerCase().includes(filteringText.toLowerCase()) ||
      (run.errorMessage?.toLowerCase().includes(filteringText.toLowerCase()) ?? false) ||
      (run.scheduleName?.toLowerCase().includes(filteringText.toLowerCase()) ?? false);

    // Status filter
    const matchesStatus =
      statusFilter.length === 0 || statusFilter.includes(run.status.toLowerCase());

    // Trigger source filter
    const matchesTrigger =
      triggerFilter.length === 0 ||
      (run.triggerSource && triggerFilter.includes(run.triggerSource.toLowerCase())) ||
      (!run.triggerSource && triggerFilter.includes("manual"));

    // Date range filter
    let matchesDate = true;
    if (dateRange && dateRange.value) {
      const runDate = new Date(run.startedAt);
      const startDate = dateRange.value.startDate ? new Date(dateRange.value.startDate) : null;
      const endDate = dateRange.value.endDate ? new Date(dateRange.value.endDate) : null;

      if (startDate && runDate < startDate) matchesDate = false;
      if (endDate && runDate > endDate) matchesDate = false;
    }

    return matchesText && matchesStatus && matchesTrigger && matchesDate;
  });

  // Sort filtered runs
  const sortedRuns = [...filteredRuns].sort((a, b) => {
    if (!sortingColumn?.sortingField) return 0;

    let aValue: any;
    let bValue: any;

    switch (sortingColumn.sortingField) {
      case "runId":
        aValue = a.runId;
        bValue = b.runId;
        break;
      case "startedAt":
        aValue = new Date(a.startedAt).getTime();
        bValue = new Date(b.startedAt).getTime();
        break;
      case "status":
        aValue = a.status;
        bValue = b.status;
        break;
      case "runType":
        aValue = a.runType;
        bValue = b.runType;
        break;
      case "executionTime":
        // Calculate execution time for sorting
        const aStart = new Date(a.startedAt).getTime();
        const aEnd = a.completedAt ? new Date(a.completedAt).getTime() : new Date().getTime();
        aValue = aEnd - aStart;

        const bStart = new Date(b.startedAt).getTime();
        const bEnd = b.completedAt ? new Date(b.completedAt).getTime() : new Date().getTime();
        bValue = bEnd - bStart;
        break;
      case "createdBy":
        aValue = a.createdBy;
        bValue = b.createdBy;
        break;
      default:
        return 0;
    }

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Compare values
    let comparison = 0;
    if (aValue < bValue) comparison = -1;
    if (aValue > bValue) comparison = 1;

    // Apply sort direction
    return sortingColumn.sortingDescending ? -comparison : comparison;
  });

  // Pagination
  const pageSize = 10;
  const pagesCount = Math.ceil(sortedRuns.length / pageSize);
  const currentPageItems = sortedRuns.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize,
  );

  // Handle sorting
  const handleSortingChange = (event: any) => {
    setSortingColumn(event.detail.sortingColumn);
  };

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Calculate execution time
  const calculateExecutionTime = (startedAt: string, completedAt?: string) => {
    try {
      const start = new Date(startedAt);
      const end = completedAt ? new Date(completedAt) : new Date();
      const diff = end.getTime() - start.getTime();

      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    } catch {
      return "-";
    }
  };

  // Status indicator component
  const StatusIcon = ({ status }: { status: JobRunStatus }) => {
    // Check if it's a failure status
    if (isFailureStatus(status)) {
      return <StatusIndicator type="error">{status.replace(/_/g, " ")}</StatusIndicator>;
    }

    // Handle specific statuses
    switch (status) {
      case "COMPLETED":
      case "SUCCEEDED":
        return <StatusIndicator type="success">Completed</StatusIndicator>;
      case "PENDING":
        return <StatusIndicator type="pending">Pending</StatusIndicator>;
      case "GENERATING_CODE":
        return <StatusIndicator type="loading">Generating Code</StatusIndicator>;
      case "SUBMITTING_QUERY":
        return <StatusIndicator type="loading">Submitting Query</StatusIndicator>;
      case "QUERY_SUBMITTED":
        return <StatusIndicator type="info">Query Submitted</StatusIndicator>;
      case "QUERY_RUNNING":
        return <StatusIndicator type="in-progress">Query Running</StatusIndicator>;
      case "CODE_RETRIEVED":
        return <StatusIndicator type="success">Code Retrieved</StatusIndicator>;
      case "RETRYING":
        return <StatusIndicator type="warning">Retrying</StatusIndicator>;
      case "PERSISTING":
        return <StatusIndicator type="in-progress">Writing to Iceberg</StatusIndicator>;
      case "VERIFYING":
        return <StatusIndicator type="loading">Verifying Data</StatusIndicator>;
      case "PUBLISHING":
        return <StatusIndicator type="loading">Publishing to Andes</StatusIndicator>;
      default:
        return <StatusIndicator type="pending">{status}</StatusIndicator>;
    }
  };

  // Run type badge with trigger source
  const RunTypeBadge = ({ run }: { run: JobRun }) => {
    // Show trigger source if available
    if (run.triggerSource === "EventBridge-Scheduled" || run.runType === "SCHEDULED") {
      return (
        <SpaceBetween size="xs" direction="horizontal">
          <Badge color="blue">
            <SpaceBetween size="xxs" direction="horizontal">
              <Icon name="calendar" />
              <span>Scheduled</span>
            </SpaceBetween>
          </Badge>
          {run.scheduleId && (
            <Link
              href="#"
              external={false}
              fontSize="body-s"
              onFollow={(e) => {
                e.preventDefault();
                navigate(`/profile/${profileId}/job/${jobId}/schedules/${run.scheduleId}`);
              }}
            >
              View Schedule
            </Link>
          )}
        </SpaceBetween>
      );
    }

    if (run.runType === "ADHOC") {
      return <Badge color="grey">Ad-hoc</Badge>;
    }

    return <Badge>Manual</Badge>;
  };

  // Table column definitions
  const columnDefinitions = [
    {
      id: "runId",
      header: "Run ID",
      cell: (item: JobRun) => (
        <Link
          href="#"
          external={false}
          onFollow={(e) => {
            e.preventDefault();
            // Navigate to run details
            navigate(`/profile/${profileId}/job/${jobId}/run/${item.runId}`);
          }}
        >
          {item.runId.substring(0, 8)}...
        </Link>
      ),
      sortingField: "runId",
      isRowHeader: true,
      width: 150,
    },
    {
      id: "startedAt",
      header: "Started At",
      cell: (item: JobRun) => formatDate(item.startedAt),
      sortingField: "startedAt",
      width: 200,
    },
    {
      id: "status",
      header: "Status",
      cell: (item: JobRun) => <StatusIcon status={item.status} />,
      sortingField: "status",
      width: 120,
    },
    {
      id: "persistence",
      header: "Persistence",
      cell: (item: JobRun) => {
        if (!item.persistenceStatus) return "-";
        const colorMap: Record<string, "green" | "grey" | "red"> = {
          PERSISTED: "green",
          SKIPPED: "grey",
          FAILED: "red",
        };
        return (
          <SpaceBetween size="xxs" direction="vertical">
            <SpaceBetween size="xxs" direction="horizontal">
              <Badge color={colorMap[item.persistenceStatus] || "grey"}>
                {item.persistenceStatus}
              </Badge>
              {item.partitionValue && (
                <Box fontSize="body-s" color="text-body-secondary">
                  {item.partitionValue}
                </Box>
              )}
            </SpaceBetween>
            {item.persistenceStatus === "FAILED" && item.persistenceError && (
              <Box fontSize="body-s" color="text-status-error">
                {item.persistenceError.length > 80
                  ? item.persistenceError.substring(0, 80) + "…"
                  : item.persistenceError}
              </Box>
            )}
          </SpaceBetween>
        );
      },
      width: 200,
    },
    {
      id: "andesStatus",
      header: "Andes",
      cell: (item: JobRun) => {
        if (!item.andesRegistrationStatus) return "-";
        const { color, label } = getAndesStatus(item.andesRegistrationStatus);
        const badge = <Badge color={color}>{label || item.andesRegistrationStatus}</Badge>;
        const errorText = item.andesRegistrationError ? (
          <Box fontSize="body-s" color="text-status-error">
            {item.andesRegistrationError.length > 80
              ? item.andesRegistrationError.substring(0, 80) + "…"
              : item.andesRegistrationError}
          </Box>
        ) : null;

        if (item.datasetUrl && item.andesRegistrationStatus !== "FAILED") {
          return (
            <SpaceBetween size="xxs" direction="vertical">
              <Link href={item.datasetUrl} external={true}>
                {badge}
              </Link>
              {errorText}
            </SpaceBetween>
          );
        }
        return (
          <SpaceBetween size="xxs" direction="vertical">
            {badge}
            {errorText}
          </SpaceBetween>
        );
      },
      width: 120,
    },
    {
      id: "runType",
      header: "Trigger",
      cell: (item: JobRun) => <RunTypeBadge run={item} />,
      sortingField: "runType",
      width: 200,
    },
    {
      id: "executionTime",
      header: "Duration",
      cell: (item: JobRun) => calculateExecutionTime(item.startedAt, item.completedAt),
      sortingField: "executionTime",
      width: 120,
    },
    {
      id: "createdBy",
      header: "Created By",
      cell: (item: JobRun) => (
        <Link href={`https://phonetool.amazon.com/users/${item.createdBy}`} external={true}>
          @{item.createdBy}
        </Link>
      ),
      sortingField: "createdBy",
      width: 150,
    },
    {
      id: "actions",
      header: "Actions",
      cell: (item: JobRun) => (
        <SpaceBetween size="xs" direction="horizontal">
          <Button
            variant="link"
            iconName="external"
            onClick={() => navigate(`/profile/${profileId}/job/${jobId}/run/${item.runId}`)}
          >
            View
          </Button>
          {item.status === "SUCCEEDED" && item.persistenceStatus !== "PERSISTED" && (
            <Button
              variant="link"
              iconName="upload"
              loading={publishingRunId === item.runId}
              disabled={publishingRunId !== null}
              onClick={() => startPublishFlow(item.runId, item.jobId, "Failed to publish")}
            >
              Publish
            </Button>
          )}
          {item.status === "SUCCEEDED" &&
            item.persistenceStatus === "PERSISTED" &&
            item.andesRegistrationStatus === "FAILED" && (
              <Button
                variant="link"
                iconName="refresh"
                loading={publishingRunId === item.runId}
                disabled={publishingRunId !== null}
                onClick={() =>
                  startPublishFlow(item.runId, item.jobId, "Failed to retry Andes registration")
                }
              >
                Retry Andes
              </Button>
            )}
        </SpaceBetween>
      ),
      width: 200,
    },
  ];

  // Handle clear filters
  const handleClearFilters = () => {
    setFilteringText("");
    setStatusFilter([]);
    setTriggerFilter([]);
    setDateRange(null);
    setCurrentPageIndex(1);
  };

  // Status filter options
  const statusOptions = [
    { label: "Success", value: "success" },
    { label: "Running", value: "running" },
    { label: "Failed", value: "failed" },
    { label: "Submitted", value: "submitted" },
  ];

  // Get active filter count
  const activeFilterCount = [
    filteringText,
    statusFilter.length > 0,
    triggerFilter.length > 0,
    dateRange?.value,
  ].filter(Boolean).length;

  return (
    <div className="job-history-page">
      <ContentLayout
        defaultPadding={false}
        headerVariant="high-contrast"
        header={
          <Box padding={{ horizontal: "xxxl", vertical: "l" }}>
            <SpaceBetween size="m" direction="vertical">
              <div style={{ display: "flex", alignItems: "center" }}>
                <BreadcrumbGroup
                  items={[
                    { text: "Home", href: "/" },
                    { text: profileData?.name || "Loading...", href: `/profile/${profileId}` },
                    { text: "Jobs", href: `/profile/${profileId}/jobs` },
                    { text: jobRuns[0]?.jobName || jobId || "Job Runs", href: "#" },
                  ]}
                  ariaLabel="Breadcrumbs"
                />
                <div
                  style={{ display: "flex", gap: "12px", marginLeft: "auto", alignItems: "center" }}
                >
                  {jobRuns[0]?.ldap && <Badge color="green">{jobRuns[0].ldap}</Badge>}
                  <Button
                    iconName="refresh"
                    onClick={handleRefresh}
                    variant="normal"
                    loading={loading}
                    ariaLabel="Refresh job runs"
                  ></Button>
                </div>
              </div>
              <SpaceBetween size="xs" direction="vertical">
                <Box fontSize="heading-xl" fontWeight="bold" color="inherit">
                  {jobRuns[0]?.jobName || jobId || "Job Run History"}
                </Box>
              </SpaceBetween>
              <Box color="inherit" fontWeight="light">
                <em>Below table provides information on all the historic runs for this job</em>
              </Box>
              {jobRuns[0]?.description && (
                <Box fontSize="body-s" color="inherit">
                  {jobRuns[0].description}
                </Box>
              )}
            </SpaceBetween>
          </Box>
        }
      >
        <Box padding={{ horizontal: "xxl", vertical: "l" }}>
          <Container>
            <SpaceBetween size="l" direction="vertical">
              {/* Filters */}
              {/* Error Alert */}
              {error && (
                <Alert
                  type="error"
                  dismissible
                  onDismiss={() => setError(null)}
                  header="Error loading job runs"
                >
                  {error}
                </Alert>
              )}

              {/* Filters */}
              <div className="filters-section">
                <SpaceBetween size="m" direction="vertical">
                  <TextFilter
                    filteringText={filteringText}
                    filteringPlaceholder="Find job runs by ID, user, schedule name or any other information available below table"
                    filteringAriaLabel="Filter job runs"
                    onChange={({ detail }) => {
                      setFilteringText(detail.filteringText);
                      setCurrentPageIndex(1);
                    }}
                    countText={`${sortedRuns.length} matches`}
                  />

                  {(statusFilter.length > 0 || triggerFilter.length > 0 || dateRange) && (
                    <SpaceBetween size="s" direction="horizontal">
                      {statusFilter.length > 0 && (
                        <div className="status-filter-group">
                          <span className="filter-label">Status: {statusFilter.join(", ")}</span>
                          <Button
                            iconName="close"
                            variant="inline-icon"
                            ariaLabel="Remove status filter"
                            onClick={() => setStatusFilter([])}
                          />
                        </div>
                      )}

                      {triggerFilter.length > 0 && (
                        <div className="trigger-filter-group">
                          <span className="filter-label">Trigger: {triggerFilter.join(", ")}</span>
                          <Button
                            iconName="close"
                            variant="inline-icon"
                            ariaLabel="Remove trigger filter"
                            onClick={() => setTriggerFilter([])}
                          />
                        </div>
                      )}

                      {(statusFilter.length > 0 || triggerFilter.length > 0) && dateRange && (
                        <span className="filter-separator">and</span>
                      )}

                      {dateRange && (
                        <div className="date-filter-group">
                          <span className="filter-label">
                            Date: {dateRange.value?.startDate || ""} -{" "}
                            {dateRange.value?.endDate || ""}
                          </span>
                          <Button
                            iconName="close"
                            variant="inline-icon"
                            ariaLabel="Remove date filter"
                            onClick={() => setDateRange(null)}
                          />
                        </div>
                      )}

                      {activeFilterCount > 0 && (
                        <Button onClick={handleClearFilters} variant="link">
                          Clear all filters
                        </Button>
                      )}
                    </SpaceBetween>
                  )}
                </SpaceBetween>
              </div>

              {/* Table */}
              <Table
                columnDefinitions={columnDefinitions}
                items={currentPageItems}
                loading={loading}
                loadingText="Loading job runs"
                onSortingChange={handleSortingChange}
                sortingColumn={sortingColumn}
                trackBy="runId"
                variant="container"
                stickyHeader={true}
                empty={
                  <Box
                    textAlign="center"
                    color="text-status-inactive"
                    padding={{ vertical: "xxl" }}
                  >
                    <SpaceBetween size="m">
                      <Icon name="search" size="large" />
                      <b>No job runs found</b>
                      <Box>No runs match the current filters</Box>
                    </SpaceBetween>
                  </Box>
                }
                header={
                  <Header
                    counter={`(${sortedRuns.length})`}
                    actions={
                      <Pagination
                        currentPageIndex={currentPageIndex}
                        pagesCount={Math.max(1, pagesCount)}
                        onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                        ariaLabels={{
                          nextPageLabel: "Next page",
                          previousPageLabel: "Previous page",
                          pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
                        }}
                      />
                    }
                  >
                    Job Run History
                  </Header>
                }
              />

              {/* Load More Button */}
              {hasMore && !loading && (
                <Box textAlign="center">
                  <Button onClick={handleLoadMore} loading={loading} loadingText="Loading more...">
                    Load more job runs
                  </Button>
                </Box>
              )}
            </SpaceBetween>
          </Container>
          {/* Add spacing at bottom to prevent footer overlap */}
          <Box padding={{ bottom: "xxxl" }} />
        </Box>
      </ContentLayout>
    </div>
  );
};

export default JobHistory;
